import { Button, Divider, Grid, Stack, Typography } from '@vapor/material'
import { useCallback, useMemo, useState } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DocumentCustomerInput, { DocumentCustomerError } from '../../../../../components/document-customer-input/document-customer-input'
import { DocumentCustomerFormSchema } from '../../../../../components/document-customer-input/document-customer-schema'
import { CoData } from './co-query-wrapper'
import { CustomerDetailFragment, ReferenceCustomerFragment } from 'src/api/api.generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import CustomerDetail from 'src/app/pages/common/customer/customer-detail'
import { faWarning } from '@fortawesome/pro-light-svg-icons'

export type CoCustomerProps = {
  queryData: CoData
  value: DocumentCustomerFormSchema | null
  customerCurrentData: ReferenceCustomerFragment | null
  onChange: (value: DocumentCustomerFormSchema) => void
  error?: FieldError | DocumentCustomerError
  allowEditCustomer: boolean
  referenceCustomers: ReferenceCustomerFragment[] | undefined
  syncData: boolean
  setSyncData: (value: boolean) => void
  setIsTouched: (value: boolean) => void
  createReceiptZero?: boolean
}

export default function CoCustomer({
  queryData,
  value,
  customerCurrentData,
  onChange,
  error,
  allowEditCustomer,
  referenceCustomers,
  syncData,
  setSyncData,
  setIsTouched,
  createReceiptZero,
}: CoCustomerProps) {
  const { t } = useTranslation()
  const [openEditCustomer, setOpenEditCustomer] = useState(false)

  const [customerLastChanges, setCustomerLastChanges] = useState<CustomerDetailFragment>()
  //const [isTouched, setIsTouched] = useState(false)

  const onChangeHandler = useCallback(
    (value: DocumentCustomerFormSchema) => {
      onChange(value)
    },
    [onChange],
  )

  const isTouched = useMemo(() => {
    if (customerCurrentData !== null && value !== null) {
      const result = hasCustomerDataChanged(customerCurrentData, value)
      setIsTouched(result)
      return result
    }
  }, [value, customerCurrentData])

  return (
    <>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="label">{t('pms.common.checkout_document.amounts.customer')}</Typography>
          <Stack direction="row" spacing={1}>
            {allowEditCustomer && (
              <Button size="small" variant="outlined" onClick={() => setOpenEditCustomer(true)}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <FontAwesomeIcon icon={faPen} />
                  <span>{t('commons.edit')}</span>
                </Stack>
              </Button>
            )}
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        {referenceCustomers && (
          <DocumentCustomerInput //
            referenceCustomers={referenceCustomers.filter((dc) => !dc.dummyCustomer)}
            customerCurrentData={customerLastChanges || customerCurrentData}
            value={value}
            onChange={onChangeHandler}
            error={error}
            readonly={queryData.mode === 'READ_ONLY'}
            syncData={syncData}
            dataSynched={() => setSyncData(false)}
            createReceiptZero={createReceiptZero}
          />
        )}
      </Grid>
      {allowEditCustomer && isTouched && (
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} paddingY={2}>
            <FontAwesomeIcon icon={faWarning} color={'var(--warning)'} />
            <Typography variant="caption">{t('pms.accounting.documents.warning_customer_not_synced')}</Typography>
          </Stack>
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider light />
      </Grid>
      {openEditCustomer && value?.id ? (
        <CustomerDetail
          appearance={{
            type: 'DIALOG',
            open: openEditCustomer,
            onClose: () => setOpenEditCustomer(false),
            maxWidth: '600px',
          }}
          customerId={value?.id}
          onCancel={() => setOpenEditCustomer(false)}
          onUpserted={(customer) => {
            setCustomerLastChanges(customer)
            setOpenEditCustomer(false)
            setIsTouched(true)
          }}
          focusedField="ADDRESS"
        />
      ) : (
        <></>
      )}
    </>
  )
}

/**
 * Compare customer used on a document with the customer entity.
 * @param customer
 * @param documentCustomer
 * @returns `true` if the customer entity was changed after document finalization, `false` otherwise.
 */
const hasCustomerDataChanged = (customer: ReferenceCustomerFragment, documentCustomer: DocumentCustomerFormSchema): boolean => {
  return documentCustomer &&
    customer &&
    documentCustomer.id &&
    customer.id &&
    (documentCustomer?.id !== customer?.id ||
      (documentCustomer.address || undefined) !== (customer.address?.address || undefined) ||
      (documentCustomer.city || undefined) !== (customer.address?.city || undefined) ||
      (documentCustomer.countryCode || undefined) !== (customer.address?.country?.isoCode || undefined) ||
      (documentCustomer.vatNumber || undefined) !== (customer.vatNumber || undefined) ||
      (documentCustomer.fiscalCode || undefined) !== (customer.fiscalCode || undefined) ||
      (documentCustomer.municipalityId || undefined) !== (customer.address?.municipalityId ?? undefined) ||
      (documentCustomer.postalCode || undefined) !== (customer.address?.postalCode ?? undefined) ||
      (documentCustomer.province || undefined) !== (customer.address?.province ?? undefined) ||
      (documentCustomer.sdiCode || undefined) !== (customer.company?.sdiCode ?? undefined))
    ? true
    : false
}
